import { isFirefox } from "components/is_firefox";

const SCROLL_THRESHOLD_PX = 1000;
const SCROLL_THROTTLE_MS = 300;

/**
 * Leading + trailing throttle to avoid fast-scroll deadlocks.
 */
function throttle(func, limit) {
  let inThrottle;
  let hasPending;
  let lastArgs;
  let lastContext;
  return function() {
    lastArgs = arguments;
    lastContext = this;
    if (!inThrottle) {
      func.apply(lastContext, lastArgs);
      hasPending = false;
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
        if (hasPending) {
          hasPending = false;
          func.apply(lastContext, lastArgs);
        }
      }, limit);
    } else {
      hasPending = true;
    }
  };
}

/**
 * Infinite scroll with throttled scroll listener.
 *
 * @param {object} options
 * @param {Function} options.canLoad - Returns true when loading is allowed
 * @param {Function} options.onLoad  - Called when user scrolls near bottom
 * @returns {{ setupScroll: Function, cleanupScroll: Function }}
 */
export function useInfiniteScroll({ canLoad, onLoad }) {
  let scrollHandler = null;

  function handleScrollEvent() {
    if (!canLoad()) return;

    const scrollableHeight = document.body.scrollHeight - window.innerHeight - SCROLL_THRESHOLD_PX;
    const scrolledDistance = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    );

    if (scrolledDistance >= scrollableHeight) {
      onLoad();
    }
  }

  function setupScroll() {
    scrollHandler = throttle(handleScrollEvent, SCROLL_THROTTLE_MS);
    if (isFirefox()) {
      window.addEventListener('scroll', scrollHandler, true);
    } else {
      window.addEventListener('scroll', scrollHandler);
    }
  }

  function cleanupScroll() {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler, isFirefox());
      scrollHandler = null;
    }
  }

  return { setupScroll, cleanupScroll };
}
